import React, { useState, useEffect, useMemo } from "react";
import { Box, Checkbox, FormControlLabel, FormGroup, Typography, Autocomplete, TextField, Chip } from "@mui/material";
import { debounce } from "@mui/material/utils";
import ModalWithButtons from "components/molecules/Modal/ModalWithButtons";
import translations from "translations";
import ordinaryColors from "themes/ordinaryColors";
import { execGetTenantsQuery } from "graphqlBase/getTenants/__generated__/getTenants";
import { TenantRole } from "graphqlBase/types";
import { useGetUserInfo } from "lib/userInfo";

// Roles that can be granted access (excluding PLATFORM_OPERATOR as they have full access)
const GRANTABLE_ROLES = ["MANUFACTURER", "CERTIFICATION_AUTHORITY", "NETWORK_OPERATOR", "PLANNER"] as const;

type GrantableRole = (typeof GRANTABLE_ROLES)[number];

interface TenantOption {
  id: string;
  tenantName: string;
  tenantRole: string;
}

interface MatrixParameterVisibilityModalProps {
  open: boolean;
  currentRoles?: TenantRole[];
  currentTenantIds?: string[];
  onClose: () => void;
  onSave: (roles: TenantRole[], tenantIds: string[]) => void;
}

const MatrixParameterVisibilityModal: React.FC<MatrixParameterVisibilityModalProps> = ({
  open,
  currentRoles,
  currentTenantIds,
  onClose,
  onSave,
}) => {
  const userInfo = useGetUserInfo();
  const [selectedRoles, setSelectedRoles] = useState<TenantRole[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<TenantOption[]>([]);
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [tenantInputValue, setTenantInputValue] = useState("");
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingExistingTenants, setLoadingExistingTenants] = useState(false);

  // Fetch tenant details for existing grantAccessToTenantIds
  const fetchExistingTenants = async (tenantIds: string[]) => {
    if (tenantIds.length === 0) {
      setSelectedTenants([]);
      return;
    }
    setLoadingExistingTenants(true);
    try {
      const result = await execGetTenantsQuery({
        variables: {
          where: {
            id: { in: tenantIds },
          },
          take: tenantIds.length,
        },
      });
      const tenants = result.data?.tenants?.items ?? [];
      setSelectedTenants(
        tenants.map((t) => ({
          id: t.id,
          tenantName: t.tenantName,
          tenantRole: t.tenantRole,
        }))
      );
    } catch (error) {
      setSelectedTenants([]);
    } finally {
      setLoadingExistingTenants(false);
    }
  };

  // Initialize state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedRoles(currentRoles ?? []);
      fetchExistingTenants(currentTenantIds ?? []);
    }
  }, [open, currentRoles, currentTenantIds]);

  // Debounced tenant search
  const currentUserTenantId = userInfo?.tenantId;
  const fetchTenants = useMemo(
    () =>
      debounce(async (inputValue: string) => {
        if (!inputValue) {
          setTenantOptions([]);
          return;
        }
        setLoadingTenants(true);
        try {
          const result = await execGetTenantsQuery({
            variables: {
              where: {
                tenantName: { contains: inputValue },
                approvalStatus: { in: ["APPROVED", "WITHOUT_EA_APPROVAL"] },
              },
              take: 20,
            },
          });
          const tenants = result.data?.tenants?.items ?? [];
          // Filter out current user's tenant - users cannot grant visibility to themselves
          const filteredTenants = tenants.filter((t) => t.id !== currentUserTenantId);
          setTenantOptions(
            filteredTenants.map((t) => ({
              id: t.id,
              tenantName: t.tenantName,
              tenantRole: t.tenantRole,
            }))
          );
        } catch (error) {
          console.error("Error fetching tenants:", error);
          setTenantOptions([]);
        } finally {
          setLoadingTenants(false);
        }
      }, 400),
    [currentUserTenantId]
  );

  useEffect(() => {
    fetchTenants(tenantInputValue);
  }, [tenantInputValue, fetchTenants]);

  const handleRoleToggle = (role: TenantRole) => {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  };

  const handleSave = () => {
    onSave(
      selectedRoles,
      selectedTenants.map((t) => t.id)
    );
    handleClose();
  };

  const handleClose = () => {
    setSelectedRoles([]);
    setSelectedTenants([]);
    setTenantInputValue("");
    onClose();
  };

  const getRoleLabel = (role: GrantableRole): string => {
    return translations.enums.tenantRole[role] ?? role;
  };

  const hasSelections = selectedRoles.length > 0 || selectedTenants.length > 0;

  return (
    <ModalWithButtons
      openModal={open}
      onClose={handleClose}
      title={translations.pages.certificateUpload.matrixVisibility.modalTitle}
      addButtonTitle={translations.globals.button.save}
      addButtonClick={handleSave}
      cancelTitle={translations.globals.button.cancel}
      width="550px"
    >
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        {/* Description */}
        <Typography variant="body2" sx={{ color: ordinaryColors.grey700 }}>
          {translations.pages.certificateUpload.matrixVisibility.description}
        </Typography>

        {/* Role Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {translations.pages.certificateUpload.matrixVisibility.grantAccessToRoles}
          </Typography>
          <FormGroup>
            {GRANTABLE_ROLES.map((role) => (
              <FormControlLabel
                key={role}
                control={
                  <Checkbox
                    checked={selectedRoles.includes(role)}
                    onChange={() => handleRoleToggle(role)}
                    sx={{
                      color: ordinaryColors.grey500,
                      "&.Mui-checked": {
                        color: ordinaryColors.primaryBrand,
                      },
                    }}
                  />
                }
                label={getRoleLabel(role)}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Tenant Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {translations.pages.certificateUpload.matrixVisibility.grantAccessToTenants}
          </Typography>
          <Autocomplete
            multiple
            options={tenantOptions}
            value={selectedTenants}
            onChange={(_, newValue) => setSelectedTenants(newValue)}
            onInputChange={(_, newInputValue) => setTenantInputValue(newInputValue)}
            getOptionLabel={(option) => option.tenantName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loadingTenants || loadingExistingTenants}
            filterOptions={(x) => x} // Disable built-in filtering since we're doing server-side search
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={translations.pages.certificateUpload.matrixVisibility.searchTenants}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0px",
                    "& fieldset": {
                      borderColor: ordinaryColors.grey400,
                    },
                  },
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.tenantName}
                  size="small"
                  sx={{
                    backgroundColor: ordinaryColors.grey200,
                    "& .MuiChip-deleteIcon": {
                      color: ordinaryColors.grey600,
                    },
                  }}
                />
              ))
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2">{option.tenantName}</Typography>
                  <Typography variant="caption" sx={{ color: ordinaryColors.grey500 }}>
                    {translations.enums.tenantRole[option.tenantRole as GrantableRole] ?? option.tenantRole}
                  </Typography>
                </Box>
              </li>
            )}
          />
        </Box>

        {/* Summary */}
        {hasSelections && (
          <Box
            sx={{
              backgroundColor: ordinaryColors.grey200,
              padding: "0.75rem",
              borderRadius: "4px",
            }}
          >
            <Typography variant="caption" sx={{ color: ordinaryColors.grey700 }}>
              {translations.pages.certificateUpload.matrixVisibility.summary}
              {selectedRoles.length > 0 && (
                <span>
                  {" "}
                  {selectedRoles.length} {selectedRoles.length === 1 ? "role" : "roles"}
                </span>
              )}
              {selectedRoles.length > 0 && selectedTenants.length > 0 && <span>,</span>}
              {selectedTenants.length > 0 && (
                <span>
                  {" "}
                  {selectedTenants.length} {selectedTenants.length === 1 ? "tenant" : "tenants"}
                </span>
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </ModalWithButtons>
  );
};

export default MatrixParameterVisibilityModal;
